import { PenSquare } from "lucide-react";
import { MailStorageQuotaSection } from "@/features/accounts/MailStorageQuotaSection";
import { MailAccountAccordion } from "./MailAccountAccordion";
import type { AccountSettingsFocus } from "@/features/accounts/accountSettingsFocus";
import type { MailAccountSummary, MailFolder } from "@/lib/types/mail";
import type { AccountOutboxState } from "@/lib/outbox/outboxNavRows";
import { showUnifiedInboxEntry } from "@/lib/unified/constants";
import { cn } from "@/lib/cn";

type Props = {
  accounts: MailAccountSummary[];
  activeAccountId: number;
  expandedAccountIds: ReadonlySet<number>;
  selectedFolder: string;
  getAccountFolders: (accountId: number) => MailFolder[];
  isAccountFoldersLoading?: (accountId: number) => boolean;
  onToggleAccount: (accountId: number) => void;
  onSelectFolder: (accountId: number, folder: string) => void;
  onOpenSettings: (accountId: number) => void;
  onOpenAccountSettings?: (accountId: number, focus?: AccountSettingsFocus) => void;
  onCreateFolder?: (accountId: number) => void;
  onRenameFolder?: (accountId: number, folder: MailFolder) => void;
  onDeleteFolder?: (accountId: number, folder: MailFolder) => void;
  onCompose?: () => void;
  onMoveAccount?: (accountId: number, direction: "up" | "down") => void;
  unifiedUnreadCount?: number;
  getAccountOutboxState: (accountId: number) => AccountOutboxState;
  getAccountSnoozeCount?: (accountId: number) => number;
  draftSavePendingAccountId?: number | null;
  settingsVersion?: number;
};

export function MailNavSidebar({
  accounts,
  activeAccountId,
  expandedAccountIds,
  selectedFolder,
  getAccountFolders,
  isAccountFoldersLoading,
  onToggleAccount,
  onSelectFolder,
  onOpenSettings,
  onOpenAccountSettings,
  onCreateFolder,
  onRenameFolder,
  onDeleteFolder,
  onCompose,
  onMoveAccount,
  unifiedUnreadCount = 0,
  getAccountOutboxState,
  getAccountSnoozeCount,
  draftSavePendingAccountId = null,
  settingsVersion = 0,
}: Props) {
  const joinUnified = showUnifiedInboxEntry(accounts.length);
  /** Rédiger + unifié = une capsule coupée par la séparation. */
  const joinComposeToUnified = Boolean(onCompose) && joinUnified;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-[var(--mail-bg)]/40">
      {onCompose ? (
        <div className="shrink-0 px-2 pt-2 pb-0">
          <button
            type="button"
            onClick={onCompose}
            className={cn(
              "mail-btn-primary mail-nav-compose flex w-full min-w-0 items-center justify-center gap-2 border border-[var(--mail-border-soft)] px-3 py-3 text-sm font-medium",
              joinComposeToUnified
                ? "rounded-t-xl rounded-b-none border-b-0"
                : "rounded-xl"
            )}
          >
            <PenSquare className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">Rédiger</span>
          </button>
          {joinComposeToUnified ? (
            <div
              className="mail-nav-compose__rule h-px w-full bg-[var(--mail-border)]"
              aria-hidden
            />
          ) : null}
        </div>
      ) : null}

      <div className="min-h-0 flex-1 overflow-y-auto">
        <MailAccountAccordion
          accounts={accounts}
          activeAccountId={activeAccountId}
          expandedAccountIds={expandedAccountIds}
          selectedFolder={selectedFolder}
          getAccountFolders={getAccountFolders}
          isAccountFoldersLoading={isAccountFoldersLoading}
          getAccountOutboxState={getAccountOutboxState}
          getAccountSnoozeCount={getAccountSnoozeCount}
          draftSavePendingAccountId={draftSavePendingAccountId}
          onToggleAccount={onToggleAccount}
          onSelectFolder={onSelectFolder}
          onOpenSettings={onOpenSettings}
          onCreateFolder={onCreateFolder}
          onRenameFolder={onRenameFolder}
          onDeleteFolder={onDeleteFolder}
          onMoveAccount={onMoveAccount}
          unifiedUnreadCount={unifiedUnreadCount}
          settingsVersion={settingsVersion}
          omitUnifiedTopRule={joinComposeToUnified}
        />
      </div>

      <div className="shrink-0 border-t border-[var(--mail-border-soft)] px-3 py-2">
        <MailStorageQuotaSection
          accountId={activeAccountId}
          onOpenAccountSettings={
            onOpenAccountSettings
              ? (focus) => onOpenAccountSettings(activeAccountId, focus)
              : undefined
          }
        />
      </div>
    </div>
  );
}
